import { useCallback, useRef, useState } from "react";

/**
 * Pointer-driven "swipe left to reveal an action" gesture for a list row.
 * Works with touch and mouse (Pointer Events). Spread `handlers` on the
 * sliding element and apply `translateX(offset px)` to it; place the
 * revealed action behind it, `actionWidth` wide, pinned to the right.
 *
 * Returns:
 *   offset    live X translation — 0 (closed) … -actionWidth (open)
 *   dragging  true while a horizontal drag is engaged (suppress the
 *             snap transition so tracking stays 1:1 with the finger)
 *   revealed  the action is at least partially visible
 *   handlers  onPointerDown/Move/Up/Cancel to spread on the row
 *   close()   dismiss programmatically (e.g. after the action fires)
 *
 * A small horizontal threshold must be crossed before tracking starts,
 * so taps on the row's own controls still register as clicks, and a
 * mostly-vertical move is handed back to the list for scrolling.
 *
 * `onTap` fires on release when the gesture was a plain tap on a closed
 * row (not a drag, and not a tap that dismisses an open row) — use it for
 * a row-level activate (e.g. navigate) that a swipe must not trigger.
 */
export default function useSwipeToReveal({ actionWidth = 72, onTap } = {}) {
    const [offset, setOffsetState] = useState(0);
    const [dragging, setDragging] = useState(false);

    const s = useRef({
        startX: 0,
        startY: 0,
        base: 0,
        tracking: false, // pointer is down
        dragging: false, // horizontal drag engaged
        opened: false, // committed open state
        offset: 0, // latest offset, read synchronously on release
    });

    const setOffset = useCallback((v) => {
        s.current.offset = v;
        setOffsetState(v);
    }, []);

    const onPointerDown = useCallback(
        (e) => {
            const st = s.current;
            st.tracking = true;
            st.dragging = false;
            st.startX = e.clientX;
            st.startY = e.clientY;
            st.base = st.opened ? -actionWidth : 0;
        },
        [actionWidth],
    );

    const onPointerMove = useCallback(
        (e) => {
            const st = s.current;
            if (!st.tracking) return;
            const dx = e.clientX - st.startX;
            const dy = e.clientY - st.startY;
            if (!st.dragging) {
                if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) {
                    // Mostly vertical — let the list scroll, stop tracking.
                    st.tracking = false;
                    return;
                }
                if (Math.abs(dx) < 8) return;
                st.dragging = true;
                setDragging(true);
                try {
                    e.currentTarget.setPointerCapture(e.pointerId);
                } catch {
                    /* capture is best-effort */
                }
            }
            if (e.cancelable) e.preventDefault();
            setOffset(Math.max(-actionWidth, Math.min(0, st.base + dx)));
        },
        [actionWidth, setOffset],
    );

    const onPointerUp = useCallback(() => {
        const st = s.current;
        const wasDragging = st.dragging;
        const wasTracking = st.tracking;
        st.tracking = false;
        st.dragging = false;
        setDragging(false);
        if (wasDragging) {
            const shouldOpen = st.offset <= -actionWidth / 2;
            st.opened = shouldOpen;
            setOffset(shouldOpen ? -actionWidth : 0);
        } else if (st.opened) {
            // A tap on an open row dismisses it.
            st.opened = false;
            setOffset(0);
        } else if (wasTracking) {
            // A plain tap on a closed row — activate it.
            onTap?.();
        }
    }, [actionWidth, setOffset, onTap]);

    // Cancelled gesture (browser took over, e.g. scrolling): reset without
    // firing onTap, snapping back to the committed open/closed state.
    const onPointerCancel = useCallback(() => {
        const st = s.current;
        st.tracking = false;
        st.dragging = false;
        setDragging(false);
        setOffset(st.opened ? -actionWidth : 0);
    }, [actionWidth, setOffset]);

    const close = useCallback(() => {
        s.current.opened = false;
        setOffset(0);
    }, [setOffset]);

    return {
        offset,
        dragging,
        revealed: offset < 0,
        close,
        handlers: {
            onPointerDown,
            onPointerMove,
            onPointerUp,
            onPointerCancel,
        },
    };
}
